/**
 * API client for managecare-agent admin UI.
 *
 * All requests go through the Vite dev proxy at /api → http://localhost:3007.
 * In production the client is served from the same host as the API, so the
 * relative /api path just works.
 *
 * The client handles:
 *   - JSON serialization + content-type headers
 *   - Error responses → thrown ApiError with status + body
 *   - Query string building
 *   - Mode parameter (?mode=live|draft) threaded through reads
 *
 * Every method returns a Promise<data>. Errors are thrown. Loading state is
 * handled by the useApi hook in the component layer.
 */

const BASE = "/api";

export class ApiError extends Error {
  constructor(status, body, url) {
    const message = body?.message || body?.error || `HTTP ${status}`;
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.url = url;
  }
}

// Currently-active organization slug that every subsequent request
// will tag with the X-Organization header. Set by OrganizationContext
// when the user logs in or switches orgs.
let currentOrgSlug = null;
export function setCurrentOrgSlug(slug) {
  currentOrgSlug = slug || null;
}

async function request(method, path, { body, query } = {}) {
  const url = new URL(BASE + path, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") {
        url.searchParams.set(k, String(v));
      }
    }
  }
  const headers = { "Content-Type": "application/json" };
  if (currentOrgSlug) headers["X-Organization"] = currentOrgSlug;
  const opts = {
    method,
    headers,
    // Include cookies so the session flows on every request
    credentials: "include",
  };
  if (body !== undefined) {
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(url.toString(), opts);
  const contentType = res.headers.get("content-type") || "";
  const parsed = contentType.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    throw new ApiError(res.status, parsed, url.toString());
  }
  return parsed;
}

// ─── Health ──────────────────────────────────────────────────────────

export const health = () => request("GET", "/health");

// GitHub-managed content manifest. The UI reads this to disable editors
// for content owned by GitHub.
export const getGithubManagedManifest = () =>
  request("GET", "/github-managed/content");
export const getGithubManagedStatus = (opts = {}) =>
  request("GET", "/github-managed/status", { query: opts });

// ─── Auth ────────────────────────────────────────────────────────────

export const signup = (body) => request("POST", "/auth/signup", { body });
export const login = (body) => request("POST", "/auth/login", { body });
export const logout = () => request("POST", "/auth/logout");
export const getMe = () => request("GET", "/auth/me");

// ─── Organizations ───────────────────────────────────────────────────

export const listOrganizations = () => request("GET", "/organizations");
export const getOrganization = (slug) =>
  request("GET", `/organizations/${encodeURIComponent(slug)}`);
export const createOrganization = (body) =>
  request("POST", "/organizations", { body });
export const updateOrganization = (slug, body) =>
  request("PUT", `/organizations/${encodeURIComponent(slug)}`, { body });
export const deleteOrganization = (slug) =>
  request("DELETE", `/organizations/${encodeURIComponent(slug)}`);

// ─── Users ───────────────────────────────────────────────────────────

export const listAppUsers = (orgId) =>
  request("GET", "/users", { query: { orgId } });
export const createAppUser = (body) => request("POST", "/users", { body });
export const updateAppUser = (id, body) =>
  request("PUT", `/users/${encodeURIComponent(id)}`, { body });
export const deleteAppUser = (id) =>
  request("DELETE", `/users/${encodeURIComponent(id)}`);

// ─── Library: Specialty Templates ────────────────────────────────────

export const listSpecialties = () => request("GET", "/library/specialties");

// Specialty catalog (the master list of clinical specialties)
export const listSpecialtyCatalog = () =>
  request("GET", "/library/specialty-catalog");

export const getSpecialtyCatalogStats = () =>
  request("GET", "/library/specialty-catalog/stats");

export const getSpecialtyCatalogEntry = (id) =>
  request("GET", `/library/specialty-catalog/${encodeURIComponent(id)}`);

export const createSpecialtyCatalogEntry = (body) =>
  request("POST", "/library/specialty-catalog", { body });

export const updateSpecialtyCatalogEntry = (id, body) =>
  request("PUT", `/library/specialty-catalog/${encodeURIComponent(id)}`, { body });

export const deleteSpecialtyCatalogEntry = (id) =>
  request("DELETE", `/library/specialty-catalog/${encodeURIComponent(id)}`);

export const listSpecialtyAgents = (specialty, mode = "live") =>
  request("GET", `/library/specialties/${encodeURIComponent(specialty)}/agents`, { query: { mode } });

export const getSpecialtyAgent = (specialty, agent, mode = "live") =>
  request("GET", `/library/specialties/${encodeURIComponent(specialty)}/agents/${encodeURIComponent(agent)}`, { query: { mode } });

export const writeSpecialtyAgent = (specialty, agent, payload) =>
  request("PUT", `/library/specialties/${encodeURIComponent(specialty)}/agents/${encodeURIComponent(agent)}`, { body: payload });

export const getSpecialtyAgentHistory = (specialty, agent) =>
  request("GET", `/library/specialties/${encodeURIComponent(specialty)}/agents/${encodeURIComponent(agent)}/versions`);

// ─── Library: Tools catalog ─────────────────────────────────────────

export const listTools = ({ category } = {}) =>
  request("GET", "/library/tools", { query: { category } });

export const getTool = (id) =>
  request("GET", `/library/tools/${encodeURIComponent(id)}`);

export const upsertTool = (body) =>
  request("POST", "/library/tools", { body });

export const updateTool = (id, body) =>
  request("PUT", `/library/tools/${encodeURIComponent(id)}`, { body });

export const deleteTool = (id) =>
  request("DELETE", `/library/tools/${encodeURIComponent(id)}`);

// ─── Library: Agents (multi-specialty agent templates) ──────────────

export const listAgents = ({ specialty, mode = "live" } = {}) =>
  request("GET", "/library/agents", { query: { specialty, mode } });

export const getAgent = (id, mode = "live") =>
  request("GET", `/library/agents/${encodeURIComponent(id)}`, {
    query: { mode },
  });

export const writeAgent = (id, payload) =>
  request("PUT", `/library/agents/${encodeURIComponent(id)}`, { body: payload });

export const getAgentHistory = (id) =>
  request("GET", `/library/agents/${encodeURIComponent(id)}/versions`);

export const getComposedAgentPrompt = (id, mode = "draft") =>
  request("GET", `/library/agents/${encodeURIComponent(id)}/composed`, {
    query: { mode },
  });

export const publishAgent = (id) =>
  request("POST", `/library/agents/${encodeURIComponent(id)}/publish`);

// ─── Library: Platform structured agent definitions ────────────────
// 21 built-in agents (greeter, verifier, intake, closer, scheduling,
// triage, appointment-prep, referral-intake, sdoh, outbound_*, and the
// outbound task agents) stored at scope=platform-agent-def.
export const listPlatformAgentDefs = (mode = "live") =>
  request("GET", "/library/platform-agent-defs", { query: { mode } });
export const getPlatformAgentDef = (agentId, mode = "live") =>
  request("GET", `/library/platform-agent-defs/${encodeURIComponent(agentId)}`, { query: { mode } });
export const writePlatformAgentDef = (agentId, body) =>
  request("PUT", `/library/platform-agent-defs/${encodeURIComponent(agentId)}`, { body });
export const publishPlatformAgentDef = (agentId) =>
  request("POST", `/library/platform-agent-defs/${encodeURIComponent(agentId)}/publish`);
export const discardPlatformAgentDefDraft = (agentId) =>
  request("POST", `/library/platform-agent-defs/${encodeURIComponent(agentId)}/discard-draft`);
export const getPlatformAgentDefHistory = (agentId) =>
  request("GET", `/library/platform-agent-defs/${encodeURIComponent(agentId)}/versions`);

// ─── Sub-tenant structured agent definitions ───────────────────────
// Per-sub-tenant fork of the platform-agent-def. Inheritance:
// sub_tenant > platform-agent-def > in-code default.
export const listSubTenantAgentDefs = (tid, sid, mode = "draft") =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/agent-defs`, { query: { mode } });
export const getSubTenantAgentDef = (tid, sid, agentId, mode = "draft") =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/agent-defs/${encodeURIComponent(agentId)}`, { query: { mode } });
export const writeSubTenantAgentDef = (tid, sid, agentId, body) =>
  request("PUT", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/agent-defs/${encodeURIComponent(agentId)}`, { body });
export const publishSubTenantAgentDef = (tid, sid, agentId) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/agent-defs/${encodeURIComponent(agentId)}/publish`);
export const discardSubTenantAgentDefDraft = (tid, sid, agentId) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/agent-defs/${encodeURIComponent(agentId)}/discard-draft`);
export const getSubTenantAgentDefHistory = (tid, sid, agentId) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/agent-defs/${encodeURIComponent(agentId)}/versions`);

// Compiled prompt previews for the structured editor's
// Combined / Master Combined subtabs. direction = "inbound" | "outbound".
export const getPlatformAgentDefCompiledPrompt = (agentId, direction = "inbound") =>
  request("GET", `/library/platform-agent-defs/${encodeURIComponent(agentId)}/compiled-prompt`, { query: { direction } });
export const getSubTenantAgentDefCompiledPrompt = (tid, sid, agentId, direction = "inbound") =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/agents/${encodeURIComponent(agentId)}/compiled-prompt`, { query: { direction } });

export const discardAgentDraft = (id) =>
  request("POST", `/library/agents/${encodeURIComponent(id)}/discard-draft`);

export const restoreAgentVersion = (id, version, body) =>
  request(
    "POST",
    `/library/agents/${encodeURIComponent(id)}/restore/${version}`,
    { body }
  );

// ─── Library: Protocols ──────────────────────────────────────────────

export const listProtocols = ({ category, specialty, mode = "live" } = {}) =>
  request("GET", "/library/protocols", { query: { category, specialty, mode } });

export const getProtocol = (category, id, mode = "live") =>
  request(
    "GET",
    `/library/protocols/${encodeURIComponent(category)}/${encodeURIComponent(id)}`,
    { query: { mode } }
  );

export const writeProtocol = (category, id, payload) =>
  request(
    "PUT",
    `/library/protocols/${encodeURIComponent(category)}/${encodeURIComponent(id)}`,
    { body: payload }
  );

export const getProtocolHistory = (category, id) =>
  request(
    "GET",
    `/library/protocols/${encodeURIComponent(category)}/${encodeURIComponent(id)}/versions`
  );

export const restoreProtocolVersion = (category, id, version, body) =>
  request(
    "POST",
    `/library/protocols/${encodeURIComponent(category)}/${encodeURIComponent(id)}/restore/${version}`,
    { body }
  );

// ─── Protocol assignments ───────────────────────────────────────────

export const getSpecialtyAgentLinkedProtocols = (specialty, agent) =>
  request(
    "GET",
    `/library/specialties/${encodeURIComponent(specialty)}/agents/${encodeURIComponent(agent)}/linked-protocols`
  );

export const setSpecialtyAgentLinkedProtocols = (specialty, agent, linked_protocols) =>
  request(
    "PUT",
    `/library/specialties/${encodeURIComponent(specialty)}/agents/${encodeURIComponent(agent)}/linked-protocols`,
    { body: { linked_protocols, author: "admin" } }
  );

export const getSubTenantAgentLinkedProtocols = (tid, sid, agent) =>
  request(
    "GET",
    `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/agents/${encodeURIComponent(agent)}/linked-protocols`
  );

export const setSubTenantAgentLinkedProtocols = (tid, sid, agent, linked_protocols) =>
  request(
    "PUT",
    `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/agents/${encodeURIComponent(agent)}/linked-protocols`,
    { body: { linked_protocols, author: "admin" } }
  );

// ─── Pending Changes (per-doc selective publish) ─────────────────────

export const listPendingChanges = () =>
  request("GET", "/library/pending-changes");

export const getPendingChangeContent = (id) =>
  request("GET", `/library/pending-changes/${encodeURIComponent(id)}/content`);

export const publishPendingChanges = (ids) =>
  request("POST", "/library/pending-changes/publish", { body: { ids } });

export const discardPendingChanges = (ids) =>
  request("POST", "/library/pending-changes/discard", { body: { ids } });

// Per-doc protocol publish/discard (parallels platform + specialty routes)
export const publishProtocol = (category, id) =>
  request(
    "POST",
    `/library/protocols/${encodeURIComponent(category)}/${encodeURIComponent(id)}/publish`
  );

export const discardProtocolDraft = (category, id) =>
  request(
    "POST",
    `/library/protocols/${encodeURIComponent(category)}/${encodeURIComponent(id)}/discard-draft`
  );

// ─── Library Releases (snapshot-based draft/live) ────────────────────

export const publishLibrary = ({ author = "admin", notes = "" } = {}) =>
  request("POST", "/library/publish", { body: { author, notes } });

export const listLibraryReleases = ({ limit = 50 } = {}) =>
  request("GET", "/library/releases", { query: { limit } });

export const getCurrentLibraryRelease = () =>
  request("GET", "/library/releases/current");

export const getLibraryRelease = (version) =>
  request("GET", `/library/releases/${version}`);

export const revertLibraryRelease = (version, { author = "admin" } = {}) =>
  request("POST", `/library/releases/${version}/revert`, { body: { author } });

// Prod pod: materialized snapshot of each library collection tagged
// with release_version and draft_version_ref. The current pod is the
// one with is_current=true. Past releases live in the same collections
// filtered by release_version.
export const getCurrentProdPod = () =>
  request("GET", "/library/prod-pod/current");

export const getProdPodRelease = (version) =>
  request("GET", `/library/prod-pod/releases/${version}`);

// ─── Tenants ─────────────────────────────────────────────────────────

export const listTenants = () => request("GET", "/tenants");
export const getTenant = (tid) => request("GET", `/tenants/${encodeURIComponent(tid)}`);
export const createTenant = (tenant) => request("POST", "/tenants", { body: tenant });
export const updateTenant = (tid, patch) => request("PUT", `/tenants/${encodeURIComponent(tid)}`, { body: patch });
export const deleteTenant = (tid) => request("DELETE", `/tenants/${encodeURIComponent(tid)}`);

// ─── Tenant Configs ──────────────────────────────────────────────────

export const getTenantConfig = (tid, mode = "live") =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/config`, { query: { mode } });

export const writeTenantConfig = (tid, payload) =>
  request("PUT", `/tenants/${encodeURIComponent(tid)}/config`, { body: payload });

export const getTenantConfigHistory = (tid) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/config/versions`);

export const publishTenantConfig = (tid) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/config/publish`);

export const discardTenantConfigDraft = (tid) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/config/discard-draft`);

// ─── Sub-tenants ─────────────────────────────────────────────────────

export const listSubTenants = (tid) => request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants`);

export const getSubTenant = (tid, sid) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}`);

export const createSubTenant = (tid, sub) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/sub-tenants`, { body: sub });

export const updateSubTenant = (tid, sid, patch) =>
  request("PUT", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}`, { body: patch });

export const deleteSubTenant = (tid, sid) =>
  request("DELETE", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}`);

// ─── Sub-tenant Configs ──────────────────────────────────────────────

export const getSubTenantConfig = (tid, sid, mode = "live") =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/config`, { query: { mode } });

export const writeSubTenantConfig = (tid, sid, payload) =>
  request("PUT", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/config`, { body: payload });

// ─── Sub-tenant Prompts (owned, direct-edit) ─────────────────────────

export const listSubTenantPrompts = (tid, sid, mode = "live") =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/prompts`, { query: { mode } });

export const getSubTenantPrompt = (tid, sid, agent, mode = "live") =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/prompts/${encodeURIComponent(agent)}`, { query: { mode } });

export const writeSubTenantPrompt = (tid, sid, agent, payload) =>
  request("PUT", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/prompts/${encodeURIComponent(agent)}`, { body: payload });

export const getSubTenantPromptHistory = (tid, sid, agent) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/prompts/${encodeURIComponent(agent)}/versions`);

export const publishSubTenantPrompt = (tid, sid, agent) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/prompts/${encodeURIComponent(agent)}/publish`);

export const discardSubTenantPromptDraft = (tid, sid, agent) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/prompts/${encodeURIComponent(agent)}/discard-draft`);

export const restoreSubTenantPromptVersion = (tid, sid, agent, version, body) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/prompts/${encodeURIComponent(agent)}/restore/${version}`, { body });

// ─── Agent Configs ───────────────────────────────────────────────────

export const listAgentConfigs = (tid, sid, mode = "live") =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/agents`, { query: { mode } });

// Run the agent-contract validator against this sub-tenant's agents.
// Returns { results: [...per-agent...], summary: { total, passing, failing, withWarnings } }.
export const validateSubTenantAgents = (tid, sid, mode = "draft") =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/agents/validate`, { query: { mode } });

export const getAgentConfig = (tid, sid, agent, mode = "live") =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/agents/${encodeURIComponent(agent)}`, { query: { mode } });

export const writeAgentConfig = (tid, sid, agent, payload) =>
  request("PUT", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/agents/${encodeURIComponent(agent)}`, { body: payload });

// ─── Composed prompt ─────────────────────────────────────────────────

export const getComposedPrompt = (tid, { mode = "live", trackStaleness = false } = {}) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/composed-prompt`, {
    query: { mode, trackStaleness: trackStaleness ? "true" : "false" },
  });

export const getComposedDiff = (tid, from = "live", to = "draft") =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/composed-prompt/diff`, {
    query: { from, to },
  });

export const listDrafts = (tid) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/drafts`);

// ─── LLM Credentials + Cost ──────────────────────────────────────────

export const getLLMCredentials = (tid) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/llm-credentials`);

export const updateLLMCredentials = (tid, body) =>
  request("PUT", `/tenants/${encodeURIComponent(tid)}/llm-credentials`, { body });

export const getCostStatus = (tid) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/cost-status`);

export const getLLMUsage = (tid, query) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/llm-usage`, { query });

export const updateCostCaps = (tid, body) =>
  request("PUT", `/tenants/${encodeURIComponent(tid)}/cost-caps`, { body });

export const costUnlock = (tid) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/cost-unlock`);

// ─── Secrets ─────────────────────────────────────────────────────────

export const listSecrets = (tid) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/secrets`);

export const writeSecret = (tid, body) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/secrets`, { body });

export const rotateSecret = (tid, ref, body) =>
  request("PUT", `/tenants/${encodeURIComponent(tid)}/secrets/${encodeURIComponent(ref)}`, { body });

export const deleteSecret = (tid, ref) =>
  request("DELETE", `/tenants/${encodeURIComponent(tid)}/secrets/${encodeURIComponent(ref)}`);

export const testSecret = (tid, ref) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/secrets/${encodeURIComponent(ref)}/test`);

// ─── Sub-tenant LLM Credentials + Cost + Secrets + Drafts ────────────
//
// After the tenant/sub-tenant restructure each sub-tenant owns its own
// LLM credentials, cost caps, secrets, and drafts. These mirror the
// tenant-level helpers above but include the sub-tenant id in the path.

const stPath = (tid, sid) =>
  `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}`;

export const getSubTenantLLMCredentials = (tid, sid) =>
  request("GET", `${stPath(tid, sid)}/llm-credentials`);

export const updateSubTenantLLMCredentials = (tid, sid, body) =>
  request("PUT", `${stPath(tid, sid)}/llm-credentials`, { body });

export const getSubTenantCostStatus = (tid, sid) =>
  request("GET", `${stPath(tid, sid)}/cost-status`);

export const getSubTenantLLMUsage = (tid, sid, query) =>
  request("GET", `${stPath(tid, sid)}/llm-usage`, { query });

export const updateSubTenantCostCaps = (tid, sid, body) =>
  request("PUT", `${stPath(tid, sid)}/cost-caps`, { body });

export const subTenantCostUnlock = (tid, sid) =>
  request("POST", `${stPath(tid, sid)}/cost-unlock`);

export const listSubTenantSecrets = (tid, sid) =>
  request("GET", `${stPath(tid, sid)}/secrets`);

export const writeSubTenantSecret = (tid, sid, body) =>
  request("POST", `${stPath(tid, sid)}/secrets`, { body });

export const rotateSubTenantSecret = (tid, sid, ref, body) =>
  request("PUT", `${stPath(tid, sid)}/secrets/${encodeURIComponent(ref)}`, { body });

export const deleteSubTenantSecret = (tid, sid, ref) =>
  request("DELETE", `${stPath(tid, sid)}/secrets/${encodeURIComponent(ref)}`);

export const testSubTenantSecret = (tid, sid, ref) =>
  request("POST", `${stPath(tid, sid)}/secrets/${encodeURIComponent(ref)}/test`);

export const listSubTenantDrafts = (tid, sid) =>
  request("GET", `${stPath(tid, sid)}/drafts`);

// Phase 2: per-kind publish/discard for split sub-tenant config.
export const publishSubTenantOrchestration = (tid, sid) =>
  request("POST", `${stPath(tid, sid)}/orchestration/publish`);
export const discardSubTenantOrchestrationDraft = (tid, sid) =>
  request("POST", `${stPath(tid, sid)}/orchestration/discard-draft`);

export const publishSubTenantPostCall = (tid, sid) =>
  request("POST", `${stPath(tid, sid)}/post-call/publish`);
export const discardSubTenantPostCallDraft = (tid, sid) =>
  request("POST", `${stPath(tid, sid)}/post-call/discard-draft`);

export const publishSubTenantSilentConfig = (tid, sid) =>
  request("POST", `${stPath(tid, sid)}/silent-config/publish`);
export const discardSubTenantSilentConfigDraft = (tid, sid) =>
  request("POST", `${stPath(tid, sid)}/silent-config/discard-draft`);

export const listSubTenantConversations = (tid, sid, mode) =>
  request("GET", `${stPath(tid, sid)}/conversations`, { query: mode ? { mode } : {} });

export const getSubTenantConversation = (tid, sid, callId) =>
  request("GET", `${stPath(tid, sid)}/conversations/${encodeURIComponent(callId)}`);

// ─── Library Post-Call Actions ───────────────────────────────────────

export const listPostCallActions = (mode = "live") =>
  request("GET", "/library/post-call-actions", { query: { mode } });

export const getPostCallAction = (id, mode = "live") =>
  request("GET", `/library/post-call-actions/${encodeURIComponent(id)}`, { query: { mode } });

export const writePostCallAction = (id, body) =>
  request("PUT", `/library/post-call-actions/${encodeURIComponent(id)}`, { body });

export const publishPostCallAction = (id) =>
  request("POST", `/library/post-call-actions/${encodeURIComponent(id)}/publish`);

export const deletePostCallAction = (id) =>
  request("DELETE", `/library/post-call-actions/${encodeURIComponent(id)}`);

// ─── Library Chain Framework (Built-in Agents) ───────────────────────
//
// Platform-level prompts for the 9 chain framework agents (5 inbound +
// 4 outbound). Each sub-tenant gets its own editable fork seeded from
// here at creation time. Edits at this layer surface on the Library
// Updates page of every affected tenant.

export const listChainFrameworkPrompts = (mode = "live", direction) =>
  request("GET", "/library/chain-prompts", { query: { mode, ...(direction ? { direction } : {}) } });

export const getChainFrameworkPrompt = (key, mode = "live") =>
  request("GET", `/library/chain-prompts/${encodeURIComponent(key)}`, { query: { mode } });

export const writeChainFrameworkPrompt = (key, body) =>
  request("PUT", `/library/chain-prompts/${encodeURIComponent(key)}`, { body });

export const publishChainFrameworkPrompt = (key) =>
  request("POST", `/library/chain-prompts/${encodeURIComponent(key)}/publish`);

export const discardChainFrameworkPromptDraft = (key) =>
  request("POST", `/library/chain-prompts/${encodeURIComponent(key)}/discard-draft`);

export const getChainFrameworkPromptHistory = (key) =>
  request("GET", `/library/chain-prompts/${encodeURIComponent(key)}/versions`);

export const restoreChainFrameworkPromptVersion = (key, version, body = {}) =>
  request("POST", `/library/chain-prompts/${encodeURIComponent(key)}/restore/${version}`, { body });

// ─── Tool Gate Defaults (platform-wide, per agent id) ─────────────────
//
// Read-only platform defaults from configural-runtime/src/default-tool-gates.js.
// Exposed by a new admin API route below so the UI can show operators
// what the defaults are alongside any tenant override.

export const getToolGateDefaults = (agentId) =>
  request("GET", `/library/tool-gate-defaults/${encodeURIComponent(agentId)}`);

// ─── Library Impact Analysis ────────────────────────────────────────

export const getLibraryUpdates = () =>
  request("GET", "/tenants/library-updates");

export const applyLibraryUpdate = (tid, sid, body) =>
  request("POST", `${stPath(tid, sid)}/apply-library-update`, { body });

export const applyLibraryUpdatesBulk = (tid, sid, body) =>
  request("POST", `${stPath(tid, sid)}/apply-library-updates-bulk`, { body });

export const llmMerge = (body) =>
  request("POST", "/tenants/llm-merge", { body });

export const summarizeChanges = (artifacts) =>
  request("POST", "/tenants/summarize-changes", { body: { artifacts } });

// ─── Organization API Keys ──────────────────────────────────────────

export const listApiKeys = (orgSlug) =>
  request("GET", `/organizations/${encodeURIComponent(orgSlug)}/api-keys`);

export const createApiKey = (orgSlug, body) =>
  request("POST", `/organizations/${encodeURIComponent(orgSlug)}/api-keys`, { body });

export const revokeApiKey = (orgSlug, keyId) =>
  request("DELETE", `/organizations/${encodeURIComponent(orgSlug)}/api-keys/${encodeURIComponent(keyId)}`);

// ─── Export / Import (config-as-code) ───────────────────────────────

export const exportTenantUrl = (tid) =>
  `/api/tenants/${encodeURIComponent(tid)}/export`;

export const importTenantValidate = (tid, file) => {
  const form = new FormData();
  form.append("file", file);
  return fetch(`/api/tenants/${encodeURIComponent(tid)}/import`, {
    method: "POST",
    headers: { "X-Organization": localStorage.getItem("orgId") || "managecare" },
    body: form,
  }).then((r) => r.json());
};

export const importTenantApply = (tid, changes) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/import/apply`, { body: { changes } });

export const publishAllSubTenantDrafts = (tid, sid) =>
  request("POST", `${stPath(tid, sid)}/publish-all`);

export const discardAllSubTenantDrafts = (tid, sid) =>
  request("POST", `${stPath(tid, sid)}/discard-all`);

export const discardAgentConfigDraft = (tid, sid, agent) =>
  request("POST", `${stPath(tid, sid)}/agents/${encodeURIComponent(agent)}/discard-draft`);

// ─── Overrides ───────────────────────────────────────────────────────

export const listOverrides = (tid, mode = "live") =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/overrides`, { query: { mode } });

export const createOverride = (tid, body) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/overrides`, { body });

export const deactivateOverride = (tid, oid) =>
  request("DELETE", `/tenants/${encodeURIComponent(tid)}/overrides/${encodeURIComponent(oid)}`);

export const forkAgentToSubTenant = (tid, sid, agentKey) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/agents/${encodeURIComponent(agentKey)}/fork`);

export const publishOverride = (tid, oid) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/overrides/${encodeURIComponent(oid)}/publish`);

// ─── GitHub Sync API ────────────────────────────────────────────────

export const getGitHubConfig = () => request("GET", "/github/config");
export const saveGitHubConfig = (body) => request("PUT", "/github/config", { body });
export const testGitHubConnection = () => request("POST", "/github/test-connection");
export const pushToGitHub = (body) => request("POST", "/github/push", { body });
export const pullFromGitHub = () => request("POST", "/github/pull");
export const getGitHubStatus = () => request("GET", "/github/status");
export const getGitHubPushStatus = () => request("GET", "/github/push-status");
export const getGitHubPullLog = () => request("GET", "/github/pull-log");
export const getGitHubRateLimits = () => request("GET", "/github/rate-limits");
export const createGitHubRepo = (body) => request("POST", "/github/create-repo", { body });

// ─── Testing App API (external testing framework) ──────────────────
// Base URL is configurable — stored in localStorage, defaults to proxy path.
// The proxy at /testing-app → localhost:3005 is for dev convenience;
// in production the user enters the actual URL.

const TESTING_APP_STORAGE_KEY = "configural_testing_app_url";

export function getTestingAppUrl() {
  return localStorage.getItem(TESTING_APP_STORAGE_KEY) || "/testing-app";
}

export function setTestingAppUrl(url) {
  if (url && url !== "/testing-app") {
    localStorage.setItem(TESTING_APP_STORAGE_KEY, url.replace(/\/$/, ""));
  } else {
    localStorage.removeItem(TESTING_APP_STORAGE_KEY);
  }
}

async function testingRequest(method, path, body) {
  const base = getTestingAppUrl();
  const url = `${base}${path}`;
  const opts = { method, headers: { "Content-Type": "application/json" } };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(url, opts);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`Testing API ${res.status}: ${text.slice(0, 200)}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// ─── Outbound ────────────────────────────────────────────────────────

export const setSubTenantEnabledModes = (tid, sid, modes) =>
  request("PATCH", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}`, {
    body: { enabled_modes: modes },
  });

export const listOutboundCalls = (tid, sid, params = {}) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/outbound/calls`, {
    query: params,
  });

export const createOutboundCall = (tid, sid, body) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/outbound/calls`, {
    body,
  });

export const getOutboundCall = (tid, sid, id) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/outbound/calls/${encodeURIComponent(id)}`);

export const cancelOutboundCall = (tid, sid, id) =>
  request("POST", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/outbound/calls/${encodeURIComponent(id)}/cancel`, {
    body: {},
  });

// Fetch a patient by code + their pending outbound_tasks in one shot.
// Used by the "Queue Call" modal for ID-only scheduling.
export const lookupOutboundPatient = (tid, sid, patientCode) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/outbound/calls/lookup/${encodeURIComponent(patientCode)}`);

// List all patients for a sub-tenant — used by the Queue Call modal's
// patient dropdown.
export const listOutboundPatients = (tid, sid) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/outbound/calls/patients`);

// ─── Global Rules (universal + framework rule banks) ─────────────
//
// Three scopes: "universal", "framework-inbound", "framework-outbound".
// Each is a JSON array of { id, severity, text } that the compiler
// merges into every agent prompt at runtime. Resolution order:
// sub-tenant override → platform → in-code default.

export const listPlatformGlobalRules = () =>
  request("GET", "/library/global-rules");

export const getPlatformGlobalRules = (scope) =>
  request("GET", `/library/global-rules/${encodeURIComponent(scope)}`);

export const writePlatformGlobalRules = (scope, rules, opts = {}) =>
  request("PUT", `/library/global-rules/${encodeURIComponent(scope)}`, {
    body: { rules, mode: "live", author: opts.author || "ui", commit_msg: opts.commit_msg || `Edit ${scope} global rules` },
  });

export const resetPlatformGlobalRulesToDefaults = (scope, opts = {}) =>
  request("POST", `/library/global-rules/${encodeURIComponent(scope)}/reset-to-defaults`, {
    body: { author: opts.author || "ui" },
  });

export const listSubTenantGlobalRules = (tid, sid) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/global-rules`);

export const getSubTenantGlobalRules = (tid, sid, scope) =>
  request("GET", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/global-rules/${encodeURIComponent(scope)}`);

export const writeSubTenantGlobalRules = (tid, sid, scope, rules, opts = {}) =>
  request("PUT", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/global-rules/${encodeURIComponent(scope)}`, {
    body: { rules, mode: "live", author: opts.author || "ui", commit_msg: opts.commit_msg || `Edit ${scope} global rules` },
  });

export const resetSubTenantGlobalRules = (tid, sid, scope) =>
  request("DELETE", `/tenants/${encodeURIComponent(tid)}/sub-tenants/${encodeURIComponent(sid)}/global-rules/${encodeURIComponent(scope)}`);

export const testingApp = {
  // Health check — try multiple endpoints
  health: async () => {
    try {
      return await testingRequest("GET", "/health");
    } catch {
      try { return await testingRequest("GET", "/api/v1/orgs"); } catch { return null; }
    }
  },

  // Organizations
  listOrgs: () => testingRequest("GET", "/api/v1/orgs"),
  createOrg: (body) => testingRequest("POST", "/api/v1/orgs", body),

  // Tenants
  listTenants: (orgId) => testingRequest("GET", `/api/v1/orgs/${orgId}/tenants`),
  createTenant: (orgId, body) => testingRequest("POST", `/api/v1/orgs/${orgId}/tenants`, body),

  // Sub-Tenants
  listSubTenants: (orgId, tenantId) =>
    testingRequest("GET", `/api/v1/orgs/${orgId}/tenants/${tenantId}/subtenants`),
  createSubTenant: (orgId, tenantId, body) =>
    testingRequest("POST", `/api/v1/orgs/${orgId}/tenants/${tenantId}/subtenants`, body),
  updateSubTenant: (orgId, tenantId, subId, body) =>
    testingRequest("PUT", `/api/v1/orgs/${orgId}/tenants/${tenantId}/subtenants/${subId}`, body),
};
